import { ReactComponent as PlusIcon } from '@percent/cause-dashboard/common/assets/images/plus-icon.svg'
import {
  POSTHOG_FEATURE_FLAGS,
  SignUpReason,
  isStatusCheckReason,
  usePostHogFeatureFlagVariant,
  useFeatureFlagsReadyWithTimeout,
  useSignUpAttribution
} from '@percent/cause-dashboard/common/hooks'
import { StatusCheckVariant } from './StatusCheckVariant'
import { brandedSignupPartners } from '@percent/cause-dashboard/constants/brandedSignupPartners'
import { Box, Flex, Loader, Logo, Text } from '@percent/lemonade'
import { Trans, useTranslation } from 'react-i18next'
import { Footer } from '../footer/Footer'
import styles from './TitleAndPartner.module.scss'

export function TitleAndPartner() {
  const { t } = useTranslation()
  const { signUpAttribution } = useSignUpAttribution()
  const statusCheckVariant = usePostHogFeatureFlagVariant(POSTHOG_FEATURE_FLAGS.CP_AB_TESTING_STATUS_CHECK_PAGE)
  const areFlagsReady = useFeatureFlagsReadyWithTimeout(1000)

  const brandedSignUpPartner = brandedSignupPartners[signUpAttribution?.partnerId as string]

  const content = ((): {
    typography: JSX.Element
    isPartnerBranded: boolean
  } => {
    if (brandedSignUpPartner?.name === 'OpenAI' && signUpAttribution?.reason === SignUpReason.REVERIFICATION) {
      return {
        typography: (
          <>
            <Text className={styles.titleStyle}>{t('typography.oneStepSignUp.OpenAI.reverify.title')}</Text>
            <Text className={styles.descriptionStyle}>{t('typography.oneStepSignUp.OpenAI.reverify.paragraph1')}</Text>
            <Text className={styles.descriptionStyle}>{t('typography.oneStepSignUp.OpenAI.reverify.paragraph2')}</Text>
          </>
        ),
        isPartnerBranded: true
      }
    }
    if (brandedSignUpPartner?.name === 'Asana' && signUpAttribution?.reason === SignUpReason.REVERIFICATION) {
      return {
        typography: (
          <>
            <Text className={styles.titleStyle}>{t('typography.oneStepSignUp.Asana.reverify.title')}</Text>
            <Text className={styles.descriptionStyle}>
              <Box>
                <Trans i18nKey="typography.oneStepSignUp.Asana.reverify.paragraph1" />
              </Box>
            </Text>
          </>
        ),
        isPartnerBranded: true
      }
    }
    if (brandedSignUpPartner?.name === 'Canva' && isStatusCheckReason(signUpAttribution?.reason)) {
      return {
        typography: (
          <>
            <Text className={styles.titleStyle}>{t('typography.oneStepSignUp.Canva.statusCheck.title')}</Text>
            <Text className={styles.descriptionStyle}>
              {t('typography.oneStepSignUp.Canva.statusCheck.paragraph1')}
            </Text>
          </>
        ),
        isPartnerBranded: true
      }
    }
    if (brandedSignUpPartner?.name === 'Airbnb' && signUpAttribution?.reason === SignUpReason.GRANTS_ONBOARDING) {
      return {
        typography: (
          <>
            <Text className={styles.titleStyle}>{t('typography.oneStepSignUp.Airbnb.grants_onboarding.title')}</Text>
            <Text className={styles.descriptionStyle}>
              {t('typography.oneStepSignUp.Airbnb.grants_onboarding.paragraph1')}
            </Text>
            <Text className={styles.descriptionStyle}>
              {t('typography.oneStepSignUp.Airbnb.grants_onboarding.paragraph2')}
            </Text>
          </>
        ),
        isPartnerBranded: true
      }
    }
    if (brandedSignUpPartner?.name === 'Twilio' && signUpAttribution?.reason === SignUpReason.GRANTS_ONBOARDING) {
      return {
        typography: (
          <>
            <Text className={styles.titleStyle}>{t('typography.oneStepSignUp.Twilio.grants_onboarding.title')}</Text>
            <Text className={styles.descriptionStyle}>
              {t('typography.oneStepSignUp.Twilio.grants_onboarding.paragraph1')}
            </Text>
            <Text className={styles.descriptionStyle}>
              {t('typography.oneStepSignUp.Twilio.grants_onboarding.paragraph2')}
            </Text>
          </>
        ),
        isPartnerBranded: true
      }
    }
    if (brandedSignUpPartner?.name === 'Adobe' && signUpAttribution?.reason === SignUpReason.GRANTS_ONBOARDING) {
      return {
        typography: (
          <>
            <Text className={styles.titleStyle}>{t('typography.oneStepSignUp.Adobe.grants_onboarding.title')}</Text>
            <Text className={styles.descriptionStyle}>
              {t('typography.oneStepSignUp.Adobe.grants_onboarding.paragraph1')}
            </Text>
          </>
        ),
        isPartnerBranded: true
      }
    }
    if (brandedSignUpPartner?.name === 'Adyen' && signUpAttribution?.reason === SignUpReason.GRANTS_ONBOARDING) {
      return {
        typography: (
          <>
            <Text className={styles.titleStyle}>{t('typography.oneStepSignUp.Adyen.grants_onboarding.title')}</Text>
            {(['paragraph1', 'paragraph2', 'paragraph3'] as const).map(paragraph => (
              <Text className={styles.descriptionStyle} key={paragraph}>
                {t(`typography.oneStepSignUp.Adyen.grants_onboarding.${paragraph}`)}
              </Text>
            ))}
          </>
        ),
        isPartnerBranded: true
      }
    }
    if (brandedSignUpPartner?.name === 'Nike' && signUpAttribution?.reason === SignUpReason.GRANTS_ONBOARDING) {
      return {
        typography: (
          <>
            <Text className={styles.titleStyle}>{t('typography.oneStepSignUp.Nike.grants_onboarding.title')}</Text>
            {(['paragraph1', 'paragraph2', 'paragraph3'] as const).map(paragraph => (
              <Text className={styles.descriptionStyle} key={paragraph}>
                {t(`typography.oneStepSignUp.Nike.grants_onboarding.${paragraph}`)}
              </Text>
            ))}
          </>
        ),
        isPartnerBranded: true
      }
    }
    if (
      brandedSignUpPartner?.name === 'Atlassian Foundation' &&
      signUpAttribution?.reason === SignUpReason.GRANTS_ONBOARDING
    ) {
      return {
        typography: (
          <>
            <Text className={styles.titleStyle}>
              {t('typography.oneStepSignUp.AtlassianFoundation.grants_onboarding.title')}
            </Text>
            <Text className={styles.descriptionStyle}>
              {t('typography.oneStepSignUp.AtlassianFoundation.grants_onboarding.paragraph1')}
            </Text>
          </>
        ),
        isPartnerBranded: true
      }
    }
    if (
      brandedSignUpPartner?.name === 'lululemon Gives' &&
      signUpAttribution?.reason === SignUpReason.GRANTS_ONBOARDING
    ) {
      return {
        typography: (
          <>
            <Text className={styles.titleStyle}>
              {t('typography.oneStepSignUp.lululemon.grants_onboarding.paragraph1')}
            </Text>
            <Text className={styles.descriptionStyle}>
              <Trans
                i18nKey="typography.oneStepSignUp.lululemon.grants_onboarding.paragraph2"
                components={{
                  a: (
                    // eslint-disable-next-line jsx-a11y/anchor-has-content
                    <a
                      href="https://corporate.lululemon.com/lululemon-gives"
                      target="_blank"
                      rel="noopener noreferrer"
                    />
                  )
                }}
              />
            </Text>
          </>
        ),
        isPartnerBranded: true
      }
    }

    // default typography for non-branded partners
    if (signUpAttribution?.reason === SignUpReason.REVERIFICATION) {
      return {
        typography: (
          <>
            <Text className={styles.titleStyle}>{t('typography.oneStepSignUp.Goodstack.reverify.title')}</Text>
            <Text className={styles.descriptionStyle}>
              {t('typography.oneStepSignUp.Goodstack.reverify.paragraph1')}
            </Text>
          </>
        ),
        isPartnerBranded: false
      }
    }
    if (isStatusCheckReason(signUpAttribution?.reason)) {
      if (!areFlagsReady) {
        return {
          typography: <Loader isFullScreen={false} />,
          isPartnerBranded: false
        }
      }
      if (statusCheckVariant === 'test_variant') {
        return {
          typography: <StatusCheckVariant />,
          isPartnerBranded: false
        }
      }

      return {
        typography: (
          <>
            <Text className={styles.titleStyle}>{t('typography.oneStepSignUp.Goodstack.statusCheck.title')}</Text>
            <Text className={styles.descriptionStyle}>
              {t('typography.oneStepSignUp.Goodstack.statusCheck.paragraph1')}
            </Text>
          </>
        ),
        isPartnerBranded: false
      }
    }
    return {
      typography: (
        <>
          <Text className={styles.titleStyle}>{t('typography.oneStepSignUp.default.title')}</Text>
          <Text className={styles.descriptionStyle}>{t('typography.oneStepSignUp.default.paragraph1')}</Text>
        </>
      ),
      isPartnerBranded: false
    }
  })()

  return (
    <Flex className={styles.backgroundWrapper}>
      <Box className={styles.mainGrid}>
        <Box className={styles.logoContainer}>
          {content.isPartnerBranded ? (
            <>
              <Flex>
                <img
                  className={styles.partnerLogo}
                  src={brandedSignUpPartner?.logo ?? ''}
                  alt={`${brandedSignUpPartner?.name ?? 'Partner'} Logo`}
                />
              </Flex>
              <PlusIcon className={styles.plusIcon} />
              <Logo />
            </>
          ) : (
            <Logo />
          )}
        </Box>
        <Box className={styles.contentContainer}>{content.typography}</Box>
        <div className={styles.footerWrapper}>
          <Footer spread />
        </div>
      </Box>
    </Flex>
  )
}
